import { Autocomplete } from '@mui/material';
import { useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Municipality } from 'shell/models/municipality';
import debounce from 'shell/utils/debounce';
import { MunicipalityVM } from 'system/view-models/municipality';
import { ResponseAbortable } from 'shell/hooks/use-http-fetch/stateless/type';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';
import { Operation } from 'shell/view-models/operation';

interface QueryParamsMunicipality extends Record<string, string | boolean | number | undefined> {
  italia?: boolean;
  irreperibile?: boolean;
}

interface FormMunicipalityProps {
  selectedValue: MunicipalityVM | null;
  handleChange: (value: MunicipalityVM | null) => void;
  handleBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  label: string;
  error?: boolean;
  params?: QueryParamsMunicipality;
  id?: string;
}

const MunicipalityAutoComplete = ({
  selectedValue,
  handleChange,
  handleBlur = () => {
    return;
  },
  label,
  disabled,
  required = false,
  error,
  id = 'name',
  params,
}: FormMunicipalityProps): React.ReactElement => {
  const [municipalities, getMunicipalities] = useHttpFetch<
    HttpResponseList<Municipality>,
    Array<MunicipalityVM>
  >(`${__MUNICIPALITY_API_URL__}/geo/comune`, {
    options: {
      method: 'GET',
      abortable: true,
      errorCatching: true,
      loading: false,
    },
    transformer: (response) => {
      return response.data.payload.list.map((municipality) => ({
        description: municipality.nome || '',
        code: municipality.key?.codice || '',
        province: municipality.provincia || '',
        istat: municipality.codIstat,
      }));
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (municipalities.data as Array<MunicipalityVM>) || [],
      getOptionLabel: (option: MunicipalityVM) => option.description,
    }),
    [municipalities]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        if (municipalities.abort) {
          municipalities.abort();
        }

        let filters: OperationObject = {
          // nome: { value, operation: Operation.LIKE },
          nomeRicercaLike: { value, operation: Operation.LIKE },
          attivo: { value: true, operation: Operation.EQUALS },
        };

        if (params?.italia !== undefined && params?.italia !== null) {
          filters = {
            ...filters,
            italiano: { value: params?.italia, operation: Operation.EQUALS },
          };
        }

        if (params?.irreperibile !== undefined && params?.irreperibile !== null) {
          filters = {
            ...filters,
            irreperibile: { value: params?.irreperibile, operation: Operation.EQUALS },
          };
        }

        const queryParams = `${paginationListToQueryString({
          filters,
          orders: {},
        })}`;

        getMunicipalities({
          params: {},
          paramsSerializer: () => queryParams,
        }) as ResponseAbortable<HttpResponseList<Municipality>, Array<MunicipalityVM>>;
      },
      [getMunicipalities, municipalities.abort, params]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      onBlur={handleBlur}
      clearOnBlur={false}
      loading={municipalities.isLoading}
      style={{ flex: 1 }}
      renderInput={(restParams) => (
        <PmiTextField
          {...restParams}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

MunicipalityAutoComplete.displayName = 'Municipality Auto Complete component';

export default MunicipalityAutoComplete;
